import { DynamicObjectInterface } from '../../sdk/type';

// Home-page reminder banner types, in DISPLAY PRIORITY order (mirrors the My
// Progress progress-bar step order: Pre-Assessment -> Check-In -> Post-Assessment).
// When the user has multiple teams/sub-challenges triggering different reminders,
// the highest-priority one wins and a single banner is shown.
export type HomeReminderType = 'PRE_ASSESSMENT' | 'CHECK_IN' | 'POST_ASSESSMENT';

// Numeric priority — lower = higher priority (shown first).
const REMINDER_PRIORITY: Record<HomeReminderType, number> = {
  PRE_ASSESSMENT: 0,
  CHECK_IN: 1,
  POST_ASSESSMENT: 2,
};

export interface HomeReminderResult {
  type: HomeReminderType;
  // The campaign to deep-link to when the user clicks the banner action.
  campaignId: string;
}

// Local-midnight epoch for a date-ish value (string | Date), or null.
const midnight = (value?: string | Date | null): number | null => {
  if (!value) return null;
  const d = new Date(value);
  if (isNaN(d.getTime())) return null;
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

const todayMidnight = (): number => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

const addDays = (base: number, days: number): number => base + days * 24 * 60 * 60 * 1000;

// Configured check-in days for a participant: the enrolled sub-challenge's list in
// MULTI mode, otherwise the base participant activity (SINGLE / legacy). Mirrors the
// resolution used elsewhere (My Progress + BE scheduler).
const checkInDaysFor = (
  config: DynamicObjectInterface | undefined,
  subChallengeId?: string,
): number[] => {
  let daysRaw: (number | string)[] = [];
  const subChallenges = config?.activities?.subChallenges as DynamicObjectInterface[] | undefined;
  if (config?.campaignMode === 'MULTI' && Array.isArray(subChallenges) && subChallengeId) {
    daysRaw = subChallenges.find(s => s.subChallengeId === subChallengeId)?.checkIn?.days ?? [];
  } else {
    daysRaw = config?.activities?.participant?.checkIn?.days ?? [];
  }
  return Array.from(new Set(daysRaw.map(d => Number(d)).filter(d => !isNaN(d) && d > 0))).sort((a, b) => a - b);
};

// Evaluate the reminder (if any) for ONE team, against the logged-in user's own
// participation. Returns null when no reminder applies for this team.
//
// - Pre-assessment: today >= teamStart + preAssessmentReminderDays AND the user has
//   not submitted pre-assessment. (No form activation — pre is always open.)
// - Check-in: any configured check-in day is open (today >= teamStart + (day-1)),
//   pre-assessment is done, and that day has not been submitted.
// - Post-assessment: today >= teamStart + (validity - postAssessmentReminderDays),
//   pre-assessment done, post not submitted. (Same window as before.)
const evaluateTeamReminder = (
  team: DynamicObjectInterface,
  config: DynamicObjectInterface | undefined,
): HomeReminderType | null => {
  const teamStart = midnight(team?.teamStartDate);
  if (!teamStart) return null; // team hasn't started — no reminders yet

  const today = todayMidnight();
  const hasPre = !!team?.weightIn?.weightInDate;
  const hasPost = !!team?.weightOut?.weightOutDate;

  const preReminderDays = config?.preAssessmentReminderDays != null ? Number(config.preAssessmentReminderDays) : null;
  const validity = config?.activities?.participant?.validity?.value
    ? Number(config.activities.participant.validity.value)
    : null;
  const postReminderDays =
    config?.postAssessmentReminderDays != null ? Number(config.postAssessmentReminderDays) : null;

  // 1) Pre-assessment reminder (highest priority)
  if (!hasPre && preReminderDays != null && !isNaN(preReminderDays)) {
    if (today >= addDays(teamStart, preReminderDays)) return 'PRE_ASSESSMENT';
  }

  // 2) Check-in reminder — only relevant once pre-assessment is done.
  if (hasPre && !hasPost) {
    const days = checkInDaysFor(config, team?.subChallengeId);
    if (days.length > 0) {
      const submittedDays = new Set(
        (Array.isArray(team?.checkIns) ? team.checkIns : [])
          .map((c: DynamicObjectInterface) => Number(c?.day))
          .filter((d: number) => !isNaN(d)),
      );
      // Any open (today >= start + day-1) and not-yet-submitted check-in day.
      const hasOpenUnsubmitted = days.some(d => today >= addDays(teamStart, d - 1) && !submittedDays.has(d));
      if (hasOpenUnsubmitted) return 'CHECK_IN';
    }
  }

  // 3) Post-assessment reminder — unchanged timing (opens validity - postReminderDays
  //    days after start), pre done, post not yet submitted.
  if (hasPre && !hasPost && validity != null && !isNaN(validity) && postReminderDays != null && !isNaN(postReminderDays)) {
    if (today >= addDays(teamStart, validity - postReminderDays)) return 'POST_ASSESSMENT';
  }

  return null;
};

// Scan every team the user participates in (across all campaigns/sub-challenges) and
// return the single highest-priority reminder to display, or null when none apply.
// `configByCampaignId` maps campaignId -> that campaign's configJson.
export const resolveHomeReminder = (
  teams: DynamicObjectInterface[],
  configByCampaignId: Record<string, DynamicObjectInterface>,
): HomeReminderResult | null => {
  let best: HomeReminderResult | null = null;
  (teams ?? []).forEach(team => {
    const type = evaluateTeamReminder(team, configByCampaignId[team?.campaignId]);
    if (!type) return;
    if (!best || REMINDER_PRIORITY[type] < REMINDER_PRIORITY[best.type]) {
      best = { type, campaignId: team?.campaignId };
    }
  });
  return best;
};
